/**
 * 3D NATIVE PRODUCT GALLERY - Custom Element
 *
 * 这是一个自定义的 Web Component，用于展示产品的 3D 模型
 * 支持材质切换、颜色选择、加载进度显示等功能
 */
var ProductGallery3D = class extends HTMLElement {
  /**
   * 构造函数 - 初始化组件
   */
  constructor() {
    super();

    // 获取画廊的唯一标识符，优先使用 data-gallery-id，其次使用 id，最后使用默认值
    this.galleryId = this.dataset.galleryId || this.id || "product-gallery-3d";

    // 从全局配置对象中获取当前画廊的配置
    this.config = window.productGallery3DConfig && window.productGallery3DConfig[this.galleryId];
    console.log("Config:", this.config);
    // 如果没有配置，直接返回，不进行初始化
    if (!this.config) return;
    this.model3dUrl = this.config.model3d_url;
    // DOM 元素引用
    this.loadingEl = this.querySelector(`#loading-${this.galleryId}`); // 加载提示元素
    this.mediaEl = this.querySelector(`#model-media-${this.galleryId}`); // 媒体容器元素

    // 配置数据
    this.settings = this.config.settings || {}; // 模型查看器设置
    this.blocks = this.config.blocks || {}; // 颜色选项配置

    // 3D 模型相关
    this.modelViewer = null; // model-viewer 实例

    // 材质引用
    this.materialMap = {};

    // 进度条相关属性（用于平滑进度更新）
    this.currentProgress = 0;
    this.targetProgress = 0;
    this.progressAnimationFrame = null;
    this.minLoadingTime = 300; // 减少最小加载时间到 300ms
    this.loadingStartTime = null;
    this.progress100Timeout = null; // 进度到100%后的超时定时器
    this.isLoadingHidden = false; // 标记loading是否已隐藏
  }

  /**
   * 当元素被插入到 DOM 时调用
   * 这是 Web Component 的生命周期方法
   */
  connectedCallback() {
    // 记录加载开始时间，用于计算最小显示时间
    this.loadingStartTime = performance.now();
    // 显示加载界面
    this.showLoading();
    // 减少延迟到 300ms，加快初始化速度
    setTimeout(() => this.init(), 300);
  }

  init() {
    // 如果有 3D 模型 URL，等待 model-viewer 脚本加载
    if (this.model3dUrl) {
      this.waitForModelViewerScript();
    } else {
      // 没有模型 URL，直接隐藏加载界面
      this.hideLoading();
    }
  }

  /**
   * 将十六进制颜色转换为线性颜色空间的基础颜色因子
   * 使用 sRGB 到线性空间的转换公式
   * @param {string} h - 十六进制颜色值（如 "#000000" 或 "000000"）
   * @param {number} a - 透明度（0-1），默认为 1
   * @returns {Array<number>} [r, g, b, a] 格式的颜色因子数组
   */
  hexToBaseColorFactor(h, a = 1) {
    // 移除 # 符号
    h = h.replace("#", "");
    // 如果是 3 位十六进制（如 "fff"），扩展为 6 位（"ffffff"）
    if (h.length === 3) h = h.replace(/(.)/g, "$1$1");

    // 提取 RGB 值并转换为线性空间
    return [
      ...[0, 2, 4].map((i) => {
        // 提取两位十六进制并转换为 0-1 范围的十进制
        const v = parseInt(h.slice(i, i + 2), 16) / 255;
        // sRGB 到线性空间的转换公式
        return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
      }),
      a, // 透明度
    ];
  }
  /**
   * 检测是否为移动设备
   * @returns {boolean} 如果是移动设备返回 true
   */
  isMobileDevice() {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (window.matchMedia && window.matchMedia("(max-width: 768px)").matches)
    );
  }

  /**
   * 应用模型查看器的设置
   * 根据配置对象设置 model-viewer 的各种属性
   */
  applySettings() {
    // 如果 model-viewer 实例或设置不存在，直接返回
    if (!this.modelViewer || !this.settings) return;

    const s = this.settings;
    const isMobile = this.isMobileDevice();

    // 自动旋转设置
    if (s.auto_rotate) {
      this.modelViewer.setAttribute("auto-rotate", "");
    } else {
      this.modelViewer.removeAttribute("auto-rotate");
    }

    // 自动旋转延迟时间（秒）- 移动端延迟更短以便快速响应
    if (s.auto_rotate_delay !== undefined) {
      const delay = isMobile ? Math.min(s.auto_rotate_delay, 1000) : s.auto_rotate_delay;
      this.modelViewer.setAttribute("auto-rotate-delay", delay);
    }

    // 每秒旋转角度
    if (s.rotation_per_second !== undefined) {
      this.modelViewer.setAttribute("rotation-per-second", s.rotation_per_second);
    }

    // 相机控制（允许用户拖拽旋转模型）
    if (s.camera_controls) {
      this.modelViewer.setAttribute("camera-controls", "");
    } else {
      this.modelViewer.removeAttribute("camera-controls");
    }

    // 交互策略（何时允许交互）- 移动端始终允许交互
    if (isMobile) {
      // 移动端强制使用 always-allow，确保触摸交互始终可用
      this.modelViewer.setAttribute("interaction-policy", "always-allow");
    } else if (s.interaction_policy) {
      this.modelViewer.setAttribute("interaction-policy", s.interaction_policy);
    }

    // 移动端额外设置：禁用默认的上下文菜单，提升触摸体验
    if (isMobile) {
      this.modelViewer.setAttribute("disable-tap", "");
      // 设置触摸交互提示
      // this.modelViewer.setAttribute("interaction-prompt", "none");
      // 优化移动端的阻尼效果 - 降低数值以提升响应速度
      this.modelViewer.setAttribute("interpolation-decay", "50");
      // 设置触摸操作的灵敏度
      this.modelViewer.setAttribute("touch-action", "pan-y");
    }

    // 视野角度（FOV）
    if (s.field_of_view !== undefined) {
      this.modelViewer.setAttribute("field-of-view", s.field_of_view + "deg");
    }

    // 相机轨道设置（控制相机位置和距离）
    if (s.camera_orbit) {
      this.modelViewer.setAttribute("camera-orbit", s.camera_orbit);
    }

    // 相机目标点（控制相机焦点位置）
    if (s.camera_target) {
      this.modelViewer.setAttribute("camera-target", s.camera_target);
    }

    // 模型缩放（通过 CSS scale 属性）
    if (s.model_scale !== undefined && s.model_scale !== 1) {
      this.modelViewer.style.transform = `scale(${s.model_scale})`;
      this.modelViewer.style.transformOrigin = "center center";
    }

    // 设置环境光照和曝光度，避免异常反射
    this.modelViewer.setAttribute("exposure", "1"); // 曝光度
    this.modelViewer.setAttribute("shadow-intensity", "1"); // 阴影强度
    this.modelViewer.setAttribute("shadow-softness", "1"); // 阴影柔和度
  }
  // 获取材质并初始化呼吸灯效果
  initMaterialMap(viewer) {
    // 获取材质数据配置
    const materialData = this.settings.material_data || "ring_front,ring_side";
    const materialNames = materialData.split(",").map((name) => name.trim());

    // 动态获取材质引用
    materialNames.forEach((name) => {
      console.log("Getting material:", name);
      const material = viewer && viewer.model && viewer.model.getMaterialByName(name);
      this.materialMap[name] = material;
    });

    // 处理呼吸灯效果
    if (this.settings.light_effect) {
      const lightDatas = this.settings.light_datas || "light_red:#000000,light_green:#000000";
      const lights = lightDatas.split(",");
      console.log("Lights:", lights);
      lights.forEach((light) => {
        const [lightName] = light.split(":").map((s) => s.trim());
        console.log("Light name:", lightName, viewer, viewer.model, viewer.model.getMaterialByName(lightName));
        const lightMaterial = viewer && viewer.model && viewer.model.getMaterialByName(lightName);
        console.log("Light material:", lightMaterial);
        if (lightMaterial) {
          // 启动呼吸灯效果
          this.startEmissiveBreathing(lightMaterial);
        }
      });
    }
  }
  /**
   * 初始化 model-viewer 实例
   * 获取材质引用，设置事件监听器，应用设置
   */
  initModelViewer() {
    // 查找 model-viewer 元素
    const viewer = this.querySelector("model-viewer");
    console.log("Viewer:", viewer);
    // 如果找不到，500ms 后重试（可能 DOM 还未完全渲染）
    if (!viewer) {
      setTimeout(() => this.initModelViewer(), 500);
      return;
    }
    console.log("Viewer:", viewer);

    // 保存 model-viewer 实例引用
    this.modelViewer = viewer;

    // 应用模型查看器设置
    this.applySettings();

    // 设置事件监听器（包括进度更新）
    this.setupEventListeners();

    // 如果模型已经加载完成（可能是缓存的情况）
    if (viewer.loaded) {
      console.log("Model already loaded (cached)...");
      // 平滑过渡到 100% 后再隐藏加载界面
      this.updateProgressSmooth(100, () => {
        this.hideLoadingWithTransition();
      });
    } else {
      console.log("Model not loaded yet, waiting for events...");
    }
  }

  /**
   * 启动材质发光呼吸效果
   * 使用正弦波函数创建平滑的亮度变化动画
   * @param {Object} mat - 材质对象
   * @param {Object} options - 选项配置
   * @param {number} options.min - 最小发光强度，默认 0.5
   * @param {number} options.max - 最大发光强度，默认 3.0
   * @param {number} options.speed - 动画速度倍数，默认 1
   * @returns {Object} 包含 stop 方法的对象，用于停止动画
   */
  startEmissiveBreathing(mat, options = {}) {
    console.log("Starting emissive breathing...", mat, options);
    // 解构选项，使用默认值
    const { min = 10, max = 100.0, speed = 10 } = options;

    // 保存基准发光颜色（用于停止时还原）
    const base = [...mat.emissiveFactor];
    // 记录动画开始时间
    let start = performance.now();
    // 动画运行标志
    let running = true;

    /**
     * 动画循环函数
     * @param {number} time - 当前时间戳
     */
    function loop(time) {
      // 如果动画已停止，退出循环
      if (!running) return;

      // 计算经过的时间（秒），乘以速度倍数
      const t = ((time - start) / 1000) * speed;
      // 使用正弦波计算当前强度，范围在 min 到 max 之间
      // Math.sin(t * Math.PI) 产生 -1 到 1 的周期变化
      // (Math.sin(t * Math.PI) + 1) / 2 转换为 0 到 1
      // 然后映射到 min 到 max 的范围
      const intensity = min + ((Math.sin(t * Math.PI) + 1) / 2) * (max - min);

      // 应用计算出的强度到材质的发光因子
      mat.setEmissiveFactor([base[0] * intensity, base[1] * intensity, base[2] * intensity]);

      // 请求下一帧动画
      requestAnimationFrame(loop);
    }

    // 启动动画循环
    requestAnimationFrame(loop);

    // 返回控制对象
    return {
      /**
       * 停止呼吸动画
       */
      stop() {
        running = false;
        // 还原到基准发光颜色
        mat.setEmissiveFactor(base);
      },
    };
  }

  // 修改方法名和增加安全性
  fetchMaterial(name) {
    if (!this.modelViewer || !this.modelViewer.model) return null;
    const material = this.modelViewer.model.getMaterialByName(name);
    if (material) {
      this.materialMap[name] = material;
    }
    return material;
  }

  switchMaterial(colorValue) {
    const blockConfig = this.blocks[colorValue];
    if (!blockConfig || !blockConfig.json_data) return;

    const block = blockConfig.json_data;

    Object.keys(this.materialMap).forEach((name) => {
      const material = this.materialMap[name] || this.fetchMaterial(name);
      const materialConfig = block[name];

      if (material && material.pbrMetallicRoughness && materialConfig) {
        const pt = material.pbrMetallicRoughness;
        if (materialConfig.RoughnessFactor !== undefined) pt.setRoughnessFactor(materialConfig.RoughnessFactor);
        if (materialConfig.MetallicFactor !== undefined) pt.setMetallicFactor(materialConfig.MetallicFactor);
        if (materialConfig.color) pt.setBaseColorFactor(this.hexToBaseColorFactor(materialConfig.color));
      }
    });
  }
  /**
   * 设置移动端触摸交互优化
   * 确保触摸时停止自动旋转，提供更好的交互体验
   */
  setupMobileTouchOptimization() {
    if (!this.isMobileDevice()) return;

    let interactionTimeout = null;
    let isTouching = false;

    // 触摸开始 - 立即响应
    this.modelViewer.addEventListener(
      "touchstart",
      (e) => {
        isTouching = true;
        // 立即停止自动旋转
        if (this.modelViewer.hasAttribute("auto-rotate")) {
          this.modelViewer.removeAttribute("auto-rotate");
        }
        // 清除之前的超时
        if (interactionTimeout) {
          clearTimeout(interactionTimeout);
        }
      },
      { passive: true }
    );

    // 触摸结束
    this.modelViewer.addEventListener(
      "touchend",
      () => {
        isTouching = false;
        // 3秒无交互后恢复自动旋转
        interactionTimeout = setTimeout(() => {
          if (this.settings.auto_rotate && !isTouching) {
            this.modelViewer.setAttribute("auto-rotate", "");
          }
        }, 3000);
      },
      { passive: true }
    );

    // 监听相机变化事件（用户开始交互）- 作为后备方案
    this.modelViewer.addEventListener("camera-change", () => {
      // 清除之前的超时
      if (interactionTimeout) {
        clearTimeout(interactionTimeout);
      }

      // 如果不在触摸状态，设置恢复自动旋转的计时器
      if (!isTouching) {
        interactionTimeout = setTimeout(() => {
          if (this.settings.auto_rotate) {
            this.modelViewer.setAttribute("auto-rotate", "");
          }
        }, 3000);
      }
    });
  }

  /**
   * 设置 model-viewer 的事件监听器
   * 监听加载进度、完成、场景就绪等事件
   */
  setupEventListeners() {
    // 监听模型加载完成事件
    this.modelViewer.addEventListener("load", () => {
      // 如果loading已经隐藏了，就不需要再处理
      if (this.isLoadingHidden) {
        console.log("Loading already hidden, skipping...");
        return;
      }

      // 清除进度100%的超时定时器（如果有）
      if (this.progress100Timeout) {
        clearTimeout(this.progress100Timeout);
        this.progress100Timeout = null;
      }

      console.log("Load event fired - hiding loading...");
      // 确保进度条平滑过渡到 100% 后再隐藏
      this.updateProgressSmooth(100, () => {
        this.hideLoadingWithTransition();
      });
    });
    console.log("Model viewer:", this.modelViewer);
    // 监听场景图就绪事件（模型结构已加载）
    this.modelViewer.addEventListener("scene-graph-ready", () => {
      console.log("Scene graph ready...");
      // 修复材质反射问题
      this.fixMaterialReflections();
      console.log("Material reflections fixed...");
      console.log("initModelViewer...");
      console.log("initModelViewer...");
      console.log("initModelViewer done...");
      this.initMaterialMap(this.modelViewer);
      // 应用默认颜色（第一个颜色选项）
      const keys = Object.keys(this.blocks);
      if (keys.length > 0) {
        const firstColorValue = this.blocks[keys[0]].color_value;
        setTimeout(() => {
          this.switchMaterial(firstColorValue);
        }, 100);
        console.log("Switching material...");
      }
    });

    // 监听加载进度事件
    this.modelViewer.addEventListener("progress", (e) => {
      // 如果事件详情中包含总进度信息
      if (e.detail?.totalProgress !== undefined) {
        // 将进度值（0-1）转换为百分比（0-100），并限制在 100 以内
        const progress = Math.min(e.detail.totalProgress * 100, 100);
        console.log("Progress:", progress);

        // 平滑更新进度条
        this.updateProgressSmooth(progress, () => {
          // 当进度到达 100% 时，设置一个超时
          // 如果 500ms 内 load 事件还没触发，就主动关闭 loading
          if (progress >= 100 && !this.isLoadingHidden) {
            // 清除之前的超时（如果有）
            if (this.progress100Timeout) {
              clearTimeout(this.progress100Timeout);
            }

            this.progress100Timeout = setTimeout(() => {
              if (!this.isLoadingHidden) {
                console.log("Progress 100% timeout - hiding loading...");
                this.hideLoadingWithTransition();
              }
            }, 500); // 进度到100%后最多再等500ms
          }
        });
      }
      console.log("Progress event handled...");
    });

    // 错误事件监听（已注释，可根据需要启用）
    // this.modelViewer.addEventListener("error", () => this.hideLoading());

    // 设置移动端触摸交互优化
    this.setupMobileTouchOptimization();

    // 设置颜色选择器的点击事件监听器
    this.setupColorSwatchClickListeners();
  }

  /**
   * 平滑更新进度条
   * 使用 requestAnimationFrame 实现平滑的进度过渡动画
   * 避免进度条突然跳跃，提供更好的用户体验
   *
   * @param {number} targetProgress - 目标进度值（0-100）
   * @param {Function|null} onComplete - 完成回调函数，当进度达到目标值时调用
   */
  updateProgressSmooth(targetProgress, onComplete = null) {
    // 限制目标进度在 0-100 范围内
    this.targetProgress = Math.max(0, Math.min(100, targetProgress));

    // 如果已有动画在进行，先取消它
    if (this.progressAnimationFrame) {
      cancelAnimationFrame(this.progressAnimationFrame);
    }

    /**
     * 动画函数
     * 使用缓动算法实现平滑过渡
     */
    const animate = () => {
      // 计算当前进度与目标进度的差值
      const diff = this.targetProgress - this.currentProgress;

      // 如果差值小于 0.1，认为已经达到目标值
      if (Math.abs(diff) < 0.1) {
        // 直接设置为目标值
        this.currentProgress = this.targetProgress;
        // 更新进度条显示
        this.updateProgressBar(this.currentProgress);
        // 清除动画帧引用
        this.progressAnimationFrame = null;
        // 如果提供了完成回调且已达到目标值，调用回调
        if (onComplete && Math.abs(this.currentProgress - this.targetProgress) < 0.1) {
          onComplete();
        }
        return;
      }

      // 平滑过渡算法：当目标是 100% 时加快速度，否则使用正常速度
      // 这样可以让进度到 100% 后更快地完成动画
      const speed = this.targetProgress === 100 ? 0.4 : 0.2;
      this.currentProgress += diff * speed;
      // 更新进度条显示
      this.updateProgressBar(this.currentProgress);

      // 请求下一帧动画
      this.progressAnimationFrame = requestAnimationFrame(animate);
    };

    // 启动动画
    this.progressAnimationFrame = requestAnimationFrame(animate);
  }

  /**
   * 更新进度条 DOM 元素的宽度
   * @param {number} progress - 进度值（0-100）
   */
  updateProgressBar(progress) {
    // 查找进度条填充元素
    const fill = this.loadingEl?.querySelector(".product-gallery-3d-native__progress-fill");
    if (fill) {
      // 限制进度值在 0-100 范围内，并设置为宽度百分比
      fill.style.width = Math.max(0, Math.min(100, progress)) + "%";
    }
  }

  /**
   * 修复材质反射问题
   * 遍历模型中的所有网格节点，调整材质属性以避免异常反射
   */
  fixMaterialReflections() {
    // 如果模型不存在，直接返回
    if (!this.modelViewer?.model) return;

    try {
      // 遍历模型中的所有节点
      this.modelViewer.model.traverse?.((node) => {
        // 只处理网格节点（包含材质）
        if (node?.isMesh && node.material) {
          const mat = node.material;

          // 修复各向异性反射问题
          // 各向异性可能导致不自然的反射效果
          if (mat.anisotropy !== undefined && mat.anisotropy > 0) {
            mat.anisotropy = 0;
          }

          // 调整环境贴图强度
          // 过高的环境贴图强度会导致过度反射
          if (mat.envMapIntensity !== undefined && mat.envMapIntensity > 2) {
            mat.envMapIntensity = 1.5;
          }

          // 确保粗糙度不会太低（避免镜面反射异常）
          // 过低的粗糙度会产生过于完美的镜面反射，看起来不自然
          if (mat.roughness !== undefined && mat.roughness < 0.1) {
            mat.roughness = 0.2;
          }

          // 标记材质需要更新，使更改生效
          mat.needsUpdate = true;
        }
      });
    } catch (e) {
      // 静默处理错误，避免影响其他功能
    }
  }

  /**
   * 显示加载界面
   * 重置进度条并显示加载元素
   */
  showLoading() {
    // 重置隐藏标记
    this.isLoadingHidden = false;
    // 移除隐藏类，显示加载界面
    this.loadingEl?.classList.remove("hidden");
    // 重置进度值
    this.currentProgress = 0;
    this.targetProgress = 0;
    // 更新进度条显示为 0%
    this.updateProgressBar(0);
  }

  /**
   * 隐藏加载界面（带过渡效果）
   * 计算剩余的最小显示时间，确保加载界面至少显示一定时间
   */
  hideLoadingWithTransition() {
    // 如果已经隐藏了，直接返回
    if (this.isLoadingHidden) return;

    // 计算已过去的时间
    const elapsed = performance.now() - this.loadingStartTime;
    // 计算剩余需要等待的时间，确保至少显示最小加载时间
    const remaining = Math.max(0, this.minLoadingTime - elapsed);
    console.log("Hide loading - elapsed:", elapsed, "remaining:", remaining);

    setTimeout(() => {
      console.log("Hiding loading now...");
      this.hideLoading();
      // 添加已加载类名，触发淡入动画
      this.mediaEl?.classList.add("loaded");
      console.log("Loaded class added...");
    }, remaining);
  }

  /**
   * 隐藏加载界面
   * 清理动画并隐藏加载元素
   */
  hideLoading() {
    // 标记为已隐藏
    this.isLoadingHidden = true;

    // 清除进度100%的超时定时器
    if (this.progress100Timeout) {
      clearTimeout(this.progress100Timeout);
      this.progress100Timeout = null;
    }

    // 如果进度动画正在进行，取消它
    if (this.progressAnimationFrame) {
      cancelAnimationFrame(this.progressAnimationFrame);
      this.progressAnimationFrame = null;
    }

    // 添加隐藏类，隐藏加载界面
    this.loadingEl?.classList.add("hidden");
  }

  /**
   * 设置颜色选择器的点击事件监听器
   * 当用户点击颜色色块时，切换模型材质颜色
   */
  setupColorSwatchClickListeners() {
    console.log("Setup color swatch click listeners");
    // 查找颜色选择器容器
    const container = this.querySelectorAll("[data-option-selector] label.color-swatch");
    console.log("Container:", container);
    // 如果容器不存在，500ms 后重试（可能 DOM 还未完全渲染）
    if (!container) {
      setTimeout(() => this.setupColorSwatchClickListeners(), 500);
      return;
    }
    console.log("Container items:", container.children);
    container.forEach((item) => {
      item.addEventListener("click", (e) => {
        console.log("Item clicked:", e.target);
        const colorValue = e.target.style.getPropertyValue("--color-value").trim();
        console.log("Color value:", colorValue);
        this.switchMaterial(colorValue);
      });
    });
  }

  /**
   * 等待 model-viewer 脚本加载完成
   * 检查 model-viewer 脚本是否已加载，如果未加载则轮询检查
   */
  waitForModelViewerScript() {
    // 设置超时，15 秒后如果还没加载完成，隐藏加载界面
    const timeout = setTimeout(() => this.hideLoading(), 15000);

    // 检查 model-viewer 脚本是否已加载
    // 通过查找 script 标签或检查 customElements 注册情况来判断
    if (
      document.querySelector('script[src*="model-viewer"]') ||
      (document.querySelector("model-viewer") && document.querySelector("model-viewer").model)
    ) {
      // 脚本已加载，直接初始化
      this.initModelViewer();
    } else {
      // 脚本未加载，开始轮询检查
      const check = setInterval(() => {
        // 每 100ms 检查一次
        if (
          document.querySelector('script[src*="model-viewer"]') ||
          (document.querySelector("model-viewer") && document.querySelector("model-viewer").model)
        ) {
          // 脚本已加载，清除定时器和超时，初始化 model-viewer
          clearInterval(check);
          clearTimeout(timeout);
          this.initModelViewer();
        }
      }, 100);

      // 10 秒后如果还没加载完成，停止检查并隐藏加载界面
      setTimeout(() => {
        clearInterval(check);
        this.hideLoading();
      }, 10000);
    }
  }
};

// 注册自定义元素
// 检查是否已注册，避免重复注册
if (!window.customElements.get("product-3d")) {
  window.customElements.define("product-3d", ProductGallery3D);
}
